import type { ChartConfiguration } from "chart.js";

export type FulfilledStatusRow = {
  fulfilled: number;
  partially_fulfilled: number;
  unfulfilled: number;
  canceled: number;
  success_rate: number | null; // 0–100, or null when there's nothing attempted
};

// Stacked status bars + a success-rate line, one bar per time bucket.
export type FulfilledBarData = {
  kind: "bar";
  buckets: string[]; // e.g. ["2024", "2025"]
  rows: FulfilledStatusRow[]; // aligned with `buckets`
};

// A single all-time pie of the status breakdown.
export type FulfilledPieData = {
  kind: "pie";
  statuses: string[]; // reporting_status keys, e.g. ["fulfilled", ...]
  values: number[]; // aligned with `statuses`
};

export type FulfilledRidesData = FulfilledBarData | FulfilledPieData;

type StatusKey = "fulfilled" | "partially_fulfilled" | "unfulfilled" | "canceled";

const STATUS_COLORS: Record<StatusKey, string> = {
  fulfilled: "#BAC8B1", // chart-positive (moss-200)
  partially_fulfilled: "#E6E6E6", // chart-muted    (fog-100)
  unfulfilled: "#404E3B", // chart-negative (forest-900)
  canceled: "#6C8480", // chart-neutral  (sage-500)
};
const STATUS_LABELS: Record<StatusKey, string> = {
  fulfilled: "Fulfilled",
  partially_fulfilled: "Partially Fulfilled",
  unfulfilled: "Unfulfilled",
  canceled: "Canceled",
};
const LINE_COLOR = "#7B9669"; // chart-line (jade-500)

export function buildChartConfig(data: FulfilledRidesData): ChartConfiguration {
  return data.kind === "pie" ? buildPieConfig(data) : buildBarConfig(data);
}

function buildPieConfig(data: FulfilledPieData): ChartConfiguration {
  const slices = data.statuses
    .map((status, index) => ({ status: status as StatusKey, value: data.values[index] }))
    .filter((slice) => slice.value > 0);

  return {
    type: "pie",
    data: {
      labels: slices.map((slice) => STATUS_LABELS[slice.status] ?? slice.status),
      datasets: [
        {
          data: slices.map((slice) => slice.value),
          backgroundColor: slices.map((slice) => STATUS_COLORS[slice.status]),
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "right" } },
    },
  };
}

function buildBarConfig(data: FulfilledBarData): ChartConfiguration {
  const { buckets, rows } = data;
  const hasPartiallyFulfilled = rows.some((row) => row.partially_fulfilled > 0);

  const statusBar = (status: StatusKey) => ({
    type: "bar" as const,
    label: STATUS_LABELS[status],
    data: rows.map((row) => row[status]),
    backgroundColor: STATUS_COLORS[status],
    stack: "statuses",
    yAxisID: "yCount",
    order: 2,
  });

  const barDatasets: ChartConfiguration["data"]["datasets"] = [
    statusBar("fulfilled"),
    ...(hasPartiallyFulfilled ? [ statusBar("partially_fulfilled") ] : []),
    statusBar("unfulfilled"),
    statusBar("canceled"),
  ];

  return {
    type: "bar",
    data: {
      labels: buckets,
      datasets: [
        ...barDatasets,
        {
          type: "line",
          label: "Success rate",
          data: rows.map((row) => row.success_rate),
          borderColor: LINE_COLOR,
          backgroundColor: LINE_COLOR,
          yAxisID: "yPercent",
          tension: 0.25,
          spanGaps: false,
          order: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: { stacked: true },
        yCount: {
          type: "linear",
          position: "left",
          stacked: true,
          beginAtZero: true,
          ticks: { precision: 0 },
          title: { display: true, text: "Rides" },
        },
        yPercent: {
          type: "linear",
          position: "right",
          beginAtZero: true,
          max: 100,
          grid: { drawOnChartArea: false },
          ticks: { callback: (value) => `${value}%` },
          title: { display: true, text: "Success rate" },
        },
      },
    },
  };
}
